/*
 * Monfiora — cart-wide free-gift controller (4 paid pieces earn a free Shell Baby / Starfish).
 * Config: window.LP_FREE_GIFT (snippets/lp-free-gift.liquid). Reads /cart.js, adds via /cart/add.js,
 * removes via /cart/change.js, and re-renders the cart-page sections from the AJAX response.
 * Works with cart_type "notification" (no drawer needed).
 *
 * Surfaces: every [data-lp-gift] module (LP + cart page), the top offer strip, a sticky mobile bar,
 * the cart-notification toast, and the [data-lpfg-notice] status line on /cart.
 *
 * R5 hardening: pickers wire only once products load; heavy collection/product fetches are deferred
 * and cached in sessionStorage; lazy images are re-kicked after a section swap.
 *
 * R6 — external cart writes (2026-09-14): a fetch + XMLHttpRequest hook on /cart/add|change|update|clear
 * schedules a re-read of /cart.js after every successful write, from any source; refreshes coalesce
 * (trailing 250 ms debounce, one queued follow-up while a read is in flight).
 *
 * R7 — gift lifecycle (2026-10-03; Razorpay Magic Cart off, Shopify checkout, BXGY repeats per order):
 *  - Paid pieces = units on lines that are NOT _free_gift lines and whose product is in collection
 *    `all-fragrances` (all five designs). Units Shopify made free = sum of 100%-percentage allocation
 *    AMOUNTS / unit price (Shopify also puts a 100% allocation of amount 0 on the "buy" line).
 *    Allowed free gift units = min(floor(paid / 4), units Shopify made free). Gift units above that are
 *    excess: removed automatically, charged units first, then newest, with a short notice.
 *  - Removals run only where the customer can see them: on /cart and while the add-to-cart toast is
 *    open. They wait until Dawn has finished its own update and the customer has stopped tapping in
 *    the cart (a removal shifts Dawn's line numbers). Our own write goes through the native fetch, so
 *    the network hook never hears it; the same removal is tried at most once per 10 s.
 *  - Copy: nothing says a free piece is in the cart unless Shopify shows a Rs. 0 unit; the claim
 *    prompt is an action ("Choose your free piece"), never "unlocked".
 *  - Dawn's pub/sub is NOT used: publishing cartUpdate would make Dawn's <cart-items> re-fetch the
 *    whole section and wipe the notice; our own swap already updates the cart sections.
 *
 * R8 — cart drawer (Phase 1, 2026-10-04; cart_type "drawer", snippets/cart-drawer.liquid):
 *  - The drawer's Check out (form CartDrawer-Form, button outside it) is held like /cart's (config
 *    checkoutForms, also read by the inline guard in snippets/lp-free-gift.liquid).
 *  - Line-item hosts are #main-cart-items AND #CartDrawer-CartItems: busy, settled, interaction and the
 *    hold lock look at both; the lock also covers every [data-lpfg-lock-scope] (drawer body).
 *  - An open drawer is a visible surface, like /cart and the open toast: removals run there.
 *  - The cart-drawer section is requested with every removal and repaired when stale (data-cart-sig).
 *  - API for other theme scripts: window.MonfioraFreeGift {getCart, getState, nextState, quiet,
 *    markClaim, claimBlocked} and a document event 'lpfg:state' {cart, state} after every render.
 * R9 — one free piece in the copy (merchant rule, 4 Oct 2026): no surface may count, invite or promote
 * more than one free piece. The claim prompt exists only while the cart holds no free unit and no
 * free-piece line at all, and always offers exactly one; once any unit is free the state is 'done'
 * and nothing shows a next step or progress. Removal maths stays uncapped (allowed above), so a cart
 * with two sets silently keeps both free pieces.
 * R10 — Phase 2 (2026-10-05; cart drawer, /cart in drawer mode, change-scent swap):
 *  - MonfioraFreeGift.hold(promise): while any hold is open, refresh() only notes that a read is due
 *    (no render, no removal, no section repair) and cartSettled() is false, so a Check out pressed
 *    meanwhile waits. When the last hold settles, one refresh runs. The swap (assets/monfiora-cart.js
 *    swapFree) adds the new free piece before it removes the old one; without the hold the short-lived
 *    second piece would paint the warning or be removed under it. Until a held promise settles (even
 *    after HOLD_CAP_MS gave reads and renders back) no free piece is removed and a Check out that
 *    waited does not go through: it says "We could not check your cart…" and the customer taps again.
 *  - The sticky bar: its tap opens the drawer (focus on the claim, else the offer panel); on /cart in
 *    drawer mode it scrolls to the page's offer panel. It renders nothing while the drawer is open,
 *    nor on /cart in drawer mode while that offer panel is on screen (it would only repeat it).
 *  - A Dawn modal closing (quick add) re-syncs the drawer's scroll lock (MonfioraCart.syncLock).
 *  - The offer collection and the pool arrive in CFG from Theme settings (snippets/lp-free-gift.liquid).
 */
(function () {
  var CFG = window.LP_FREE_GIFT;
  if (!CFG || !window.fetch) return;
  var T = CFG.text || {};
  var GIFT_PROP = CFG.giftProperty || '_free_gift';
  var THRESHOLD = CFG.threshold || 4;
  var QUIET_MS = 1000;          // Dawn debounces quantity taps by 300 ms; wait until the customer stops
  var RETRY_MS = 10000;         // the same removal is attempted at most once per 10 s
  var NOTICE_MS = 8000;         // how long the toast / sticky bar show the removal notice
  var CLAIM_BLOCK_MS = 15 * 60 * 1000;
  var SETTLE_MS = 500;          // Dawn sends a quantity change 300 ms after the last tap or blur
  var HOLD_MAX_MS = 12000;      // a held checkout gives up waiting after this and goes to /checkout
  // Forms whose Check out is held (/cart, the toast, the cart drawer); line-item hosts Dawn edits by line number.
  var CHECKOUT_FORMS = CFG.checkoutForms || ['cart', 'cart-notification-form'];
  var HOSTS = ['main-cart-items', 'CartDrawer-CartItems'];

  var products = {};      // handle -> product json
  var collSet = null;     // Set of qualifying product handles (All Fragrances)
  var lastState = null;
  var lastCart = null;
  var modules = [];
  var claiming = false;        // in-flight guard for claim()
  var catalogLoaded = false;   // collection + products fetched
  var catalogLoading = null;   // in-flight ensureCatalog() promise
  var refreshing = false;
  var refreshTimer = null;
  var pendingRefresh = false;  // a refresh was requested while one was in flight
  var toastCount = null;       // cart.item_count the open toast's "View cart (n)" was rendered with
  var toastOpening = false;    // the toast just opened: adopt its count instead of re-fetching the button
  var bubbleGuard = false;     // set while WE rewrite #cart-icon-bubble, so its observer stays quiet
  var rawFetch = null;         // the page's fetch before our hook: our own removals use it
  var reconciling = null;      // promise of the removal in flight
  var attempts = {};           // removal signature -> time of the last attempt
  var lastInteraction = 0;     // last tap / input inside the cart page's line items
  var removedNotice = null;    // { text, at, sig }
  var justClaimed = false;
  var holding = null;          // promise of a held checkout (one at a time)
  var holdSeq = 0;             // bumps on every hold and on a back/forward-cache return: stale timers do nothing
  var heldButton = null;       // { el, label }: the Check out button showing "Checking your cart…"
  var lastAdd = null;          // { json, before, at }: the last /cart/add response and the cart before it
  var claimRefusedAt = 0;      // a claim Shopify refused (e.g. sold out): say so, offer another piece
  var lastLive = '';           // the sticky bar's last announcement
  var cartDirty = false;       // a cart write landed that no /cart.js read has seen yet
  var writeAt = 0;             // when the network hook last saw a successful cart write (monotonic)
  var lastReadAt = 0;          // when the read behind lastCart started
  var stateWaiters = [];       // MonfioraFreeGift.nextState() promises waiting for a fresh read
  var holdCount = 0;           // open MonfioraFreeGift.hold() calls: no render, removal or repair meanwhile
  var HOLD_CAP_MS = 20000;     // a hold that never settles is released after this
  var holdsPending = 0;        // held promises not settled yet (a capped one too): no removal, no checkout

  function t(key, map) {
    var s = T[key] || '';
    map = map || {};
    if (!('zero' in map)) map.zero = T.zero || '';
    Object.keys(map).forEach(function (k) { s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), map[k]); });
    return s;
  }
  function pl(one, many, n, map) { map = map || {}; map.n = n; return t(n === 1 ? one : many, map); }
  function routes() { return window.routes || {}; }
  // Monotonic: a wall-clock correction must not make a fresh cart read look older than an add.
  function now() { return window.performance && typeof performance.now === 'function' ? performance.now() : Date.now(); }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function cartUrl() { return routes().cart_url || '/cart'; }
  // The line-item hosts on this page (never both: the drawer is not rendered on /cart).
  function hostEls() {
    return HOSTS.map(function (id) { return document.getElementById(id); }).filter(Boolean);
  }
  function inHost(el) {
    if (!el || !el.closest) return false;
    for (var i = 0; i < HOSTS.length; i++) if (el.closest('#' + HOSTS[i])) return true;
    return false;
  }
  function drawerEl() { return document.querySelector('cart-drawer'); }
  function drawerIsOpen() { return !!document.querySelector('cart-drawer.active'); }
  function shortTitle(it) {
    var name = String((it && it.product_title) || '').replace(/\s+Air Freshener$/i, '');
    var v = it && it.variant_title;
    return v && v !== 'Default Title' ? name + ' · ' + v : name;
  }

  /* ---------- session cache (TTL) ---------- */
  var CACHE_TTL = 15 * 60 * 1000; // 15 min — fresh enough for a session, limits stale-variant risk
  function cacheGet(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o.t !== 'number' || (Date.now() - o.t) > CACHE_TTL) return null;
      return o.v;
    } catch (e) { return null; }
  }
  function cacheSet(key, v) {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {}
  }
  function idle(fn) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 3000 });
    else setTimeout(fn, 0);
  }
  // A claim that came back charged (discount off or changed) blocks the claim prompt for 15 min.
  function claimBlocked() {
    try { var at = parseInt(sessionStorage.getItem('lpfg_claim_blocked'), 10); return !!at && Date.now() - at < CLAIM_BLOCK_MS; } catch (e) { return false; }
  }
  function blockClaims() { try { sessionStorage.setItem('lpfg_claim_blocked', String(Date.now())); } catch (e) {} }

  /* ---------- data ---------- */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }
  function loadCollection() {
    if (!CFG.collection) return Promise.resolve(null);
    var ck = 'lpfg_coll_' + CFG.collection;
    var cached = cacheGet(ck);
    if (cached) { collSet = new Set(cached); return Promise.resolve(collSet); }
    return fetch('/collections/' + CFG.collection + '/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.products) {
          var handles = d.products.map(function (p) { return p.handle; });
          collSet = new Set(handles);
          cacheSet(ck, handles);
        }
        return collSet;
      })
      .catch(function () { return null; });
  }
  function loadProducts() {
    return Promise.all((CFG.products || []).map(function (h) {
      var ck = 'lpfg_prod_' + h;
      var cached = cacheGet(ck);
      if (cached) { products[h] = cached; return Promise.resolve(); }
      return fetch('/products/' + h + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p) { products[h] = p; cacheSet(ck, p); } })
        .catch(function () {});
    }));
  }
  // Load the qualifying-collection + gift products once, on demand. Idempotent.
  // Availability may have changed (a claim was refused): drop the cached products so the pickers
  // reload what is in stock.
  var pickersStale = false;
  function forgetCatalog() {
    (CFG.products || []).forEach(function (h) { try { sessionStorage.removeItem('lpfg_prod_' + h); } catch (e) {} });
    products = {};
    catalogLoaded = false;
    pickersStale = true;
  }
  function ensureCatalog() {
    if (catalogLoaded) return Promise.resolve();
    if (catalogLoading) return catalogLoading;
    catalogLoading = Promise.all([loadCollection(), loadProducts()]).then(function () {
      catalogLoaded = true; catalogLoading = null;
    });
    return catalogLoading;
  }

  function isGiftLine(it) {
    return !!(it.properties && String(it.properties[GIFT_PROP]) === 'true');
  }
  // Units of this line that Shopify made free. The "Buy 4, Get 1 Free" discount puts a 100% allocation
  // on the free unit's line AND a 100% allocation of amount 0 on the line it was bought with (measured
  // 2026-10-03), so the units come from the amount, never from the discount type alone.
  function freeUnits(it) {
    var unit = it.original_price || it.price || 0;
    if (!unit) return 0;
    var allocs = it.line_level_discount_allocations || [], amount = 0;
    for (var i = 0; i < allocs.length; i++) {
      var app = allocs[i].discount_application || {};
      if (String(app.value_type) === 'percentage' && parseFloat(app.value) >= 100) amount += allocs[i].amount || 0;
    }
    return Math.min(it.quantity, Math.round(amount / unit));
  }

  function computeState(cart) {
    var items = cart.items || [];
    var paid = 0, giftUnits = 0, giftFree = 0, freeTotal = 0, gifts = [], byKey = {};
    items.forEach(function (it, index) {
      var inColl = collSet ? collSet.has(it.handle) : true;   // fallback: every product (all five today)
      var f = freeUnits(it);
      freeTotal += f;
      if (isGiftLine(it)) {
        giftUnits += it.quantity;
        giftFree += f;
        var g = byKey[it.key];
        if (!g) { g = byKey[it.key] = { key: it.key, quantity: 0, free: 0, index: index, title: shortTitle(it) }; gifts.push(g); }
        g.quantity += it.quantity;
        g.free += f;
        return;
      }
      if (inColl) paid += it.quantity;
    });
    var allowed = Math.min(Math.floor(paid / THRESHOLD), freeTotal);   // uncapped: two sets keep two
    var excess = Math.max(0, giftUnits - allowed);
    // R8: progress and the claim prompt exist only while nothing in the cart is free or a free-piece
    // line; they count toward ONE piece and never toward another.
    var open = !excess && !freeTotal && !giftUnits;
    var progress = open ? Math.min(paid, THRESHOLD) : 0;
    var claimable = open && paid >= THRESHOLD ? 1 : 0;
    // Pieces still to add: toward the one free piece, or (warn, one free-piece unit) to keep it.
    // With two or more free-piece units the warn copy never counts.
    var remaining = excess
      ? (giftUnits === 1 ? Math.max(0, THRESHOLD - paid) : 0)
      : (open && !claimable ? THRESHOLD - paid : 0);
    var removals = [];
    if (excess) {
      var left = excess;
      gifts.slice().sort(function (a, b) {
        var ca = a.quantity > a.free, cb = b.quantity > b.free;
        if (ca !== cb) return ca ? -1 : 1;                           // charged units first
        return a.index - b.index;                                    // then newest (cart.js lists newest first)
      }).forEach(function (g) {
        if (left <= 0) return;
        var take = Math.min(left, g.quantity);
        removals.push({ key: g.key, to: g.quantity - take, units: take, title: g.title });
        left -= take;
      });
    }
    return {
      state: excess ? 'warn' : claimable ? 'claim' : freeTotal ? 'done' : 'locked',
      threshold: THRESHOLD,
      paid: paid, qualifying: paid,
      giftUnits: giftUnits, giftFree: giftFree, freeTotal: freeTotal,
      allowed: allowed, excess: excess, removals: removals,
      offerOff: excess > 0 && paid >= THRESHOLD * giftUnits,          // the rule is met but Shopify charges
      progress: progress, claimable: claimable, remaining: remaining,
      cartDiscount: (cart.cart_level_discount_applications || []).length > 0
    };
  }

  /* ---------- pickers ---------- */
  function populateVariants(el) {
    var prodSel = el.querySelector('[data-lpfg-product]');
    var varSel = el.querySelector('[data-lpfg-variant]');
    if (!prodSel || !varSel) return;
    var p = prodSel.value ? products[prodSel.value] : null;
    if (!p) { varSel.innerHTML = ''; return; }
    var avail = p.variants.filter(function (v) { return v.available; });
    var list = avail.length ? avail : p.variants;
    varSel.innerHTML = list.map(function (v) {
      var lbl = (v.title && v.title !== 'Default Title') ? v.title : p.title;
      return '<option value="' + v.id + '">' + lbl + '</option>';
    }).join('');
  }
  function wireModule(el) {
    var prodSel = el.querySelector('[data-lpfg-product]');
    var addBtn = el.querySelector('[data-lpfg-add]');
    var rmBtn = el.querySelector('[data-lpfg-remove]');
    // CRITICAL: only wire the pickers once products are actually loaded — otherwise
    // an early refresh() (cartUpdate / bubble / pageshow) would lock them empty and
    // claim() could never resolve a variant.
    var productList = (CFG.products || []).filter(function (h) { return products[h]; });
    if (prodSel && productList.length && !prodSel.dataset.wired) {
      prodSel.dataset.wired = '1';
      prodSel.innerHTML = productList
        .map(function (h) { return '<option value="' + h + '">' + products[h].title + '</option>'; }).join('');
      populateVariants(el);
      prodSel.addEventListener('change', function () { populateVariants(el); });
    }
    if (addBtn && !addBtn.dataset.wired) { addBtn.dataset.wired = '1'; if (T.cta) addBtn.textContent = t('cta'); addBtn.addEventListener('click', function () { claim(el, addBtn); }); }
    if (rmBtn && !rmBtn.dataset.wired) { rmBtn.dataset.wired = '1'; if (T.remove) rmBtn.textContent = T.remove; rmBtn.addEventListener('click', function () { removeGift(rmBtn); }); }
  }

  /* ---------- render ---------- */
  function noticeActive() { return !!(removedNotice && Date.now() - removedNotice.at < NOTICE_MS); }
  function warnText(st) {
    if (st.offerOff) return t('warn_off');
    if (st.giftUnits > 1) return t('warn_extra');            // never counts pieces or says "keep both"
    return pl('warn_one', 'warn_many', st.remaining);
  }
  // Two or more free-piece units, one short: "Your free piece is no longer free" would read as if the
  // only one lost its price while another is still free.
  function warnTitle(st, key) { return st.giftUnits > 1 && !st.offerOff ? t('warn_title_extra') : t(key); }

  function renderModule(el, st) {
    var title = el.querySelector('[data-lpfg-title]');
    var sub = el.querySelector('[data-lpfg-sub]');
    var bar = el.querySelector('[data-lpfg-bar]');
    var claimEl = el.querySelector('[data-lpfg-claim]');
    var done = el.querySelector('[data-lpfg-done]');
    var warn = el.querySelector('[data-lpfg-warn]');
    // The bar measures toward one free piece only: full once it is claimable or in the cart. In warn it
    // shows the shortfall of the one free piece; with two or more free-piece units, or the discount
    // not applying, it is empty (never progress toward a second piece, never full beside a warning).
    var ratio = st.state === 'locked' ? st.progress / st.threshold
      : st.state === 'warn' ? (st.giftUnits > 1 || st.offerOff ? 0 : st.paid / st.threshold)
      : 1;
    if (bar) bar.style.width = Math.min(100, Math.round(ratio * 100)) + '%';
    if (claimEl) claimEl.hidden = true;
    if (done) done.hidden = true;
    if (warn) warn.hidden = true;
    if (sub) sub.hidden = false;
    el.setAttribute('data-state', st.state);

    if (st.state === 'done') {
      if (title) title.textContent = st.giftFree ? t('done_title_one') : t('done_other_title');
      if (done) { done.hidden = false; var dt = done.querySelector('.lpfg__done-text'); if (dt) dt.textContent = st.giftFree ? t('done') : t('done_other'); }
      if (sub) sub.hidden = true;                             // R8: no next step toward another piece
    } else if (st.state === 'warn') {
      if (title) title.textContent = warnTitle(st, 'warn_title');
      if (sub) sub.hidden = true;
      if (warn) { warn.hidden = false; var wt = warn.querySelector('.lpfg__warn-text'); if (wt) wt.textContent = warnText(st); }
    } else if (st.state === 'claim') {
      if (title) title.textContent = t('claim_title');
      if (claimBlocked()) { if (sub) sub.textContent = t('claim_failed'); }
      else {
        var refused = Date.now() - claimRefusedAt < 60000;
        if (sub) sub.textContent = refused ? t('claim_refused') : t('pick') + (st.cartDiscount && T.pick_swap ? ' ' + t('pick_swap') : '');
        if (claimEl) claimEl.hidden = false;
      }
    } else {
      if (title) title.textContent = t('locked_title');
      if (sub) sub.textContent = st.progress ? pl('remaining_one', 'remaining_many', st.remaining) : t('locked_empty');
    }
  }

  // Top offer strip (LP only): show the keep-your-gift message while a gift is charged.
  function updateOfferBar(st) {
    var el = document.querySelector('[data-lpfg-offerbar]');
    if (!el) return;
    var strip = el.closest('.lp-cp__offerbar') || el.parentElement;
    if (st.state === 'warn') {
      el.textContent = warnText(st);
      if (strip) strip.classList.add('lp-cp__offerbar--warn');
    } else {
      if (el.dataset.original != null) el.textContent = el.dataset.original;
      if (strip) strip.classList.remove('lp-cp__offerbar--warn');
    }
  }

  // The /cart status line (snippet in sections/main-cart-items.liquid). It stays until the cart
  // changes again after the notice window, so a customer who looks away still finds it.
  function cartSig(cart) { return cart ? cart.item_count + ':' + cart.total_price : ''; }
  function renderNotice(cart) {
    var host = document.querySelector('[data-lpfg-notice]');
    if (!host) return;
    // Never hidden: a status region that is display:none when its text arrives is not announced.
    // Empty draws nothing (assets/lp-free-gift.css).
    if (host.hidden) host.hidden = false;
    var keep = removedNotice && (noticeActive() || cartSig(cart) === removedNotice.sig);
    var text = keep ? removedNotice.text : '';
    if (host.textContent !== text) host.textContent = text;
    if (!keep && removedNotice && !noticeActive()) removedNotice = null;
  }
  // A status line the customer can see: the /cart one, or the drawer's while the drawer is open
  // (a closed drawer keeps its host in the DOM, but nobody reads it).
  function noticeHostVisible() {
    var host = document.querySelector('[data-lpfg-notice]');
    if (!host) return false;
    var d = host.closest ? host.closest('cart-drawer') : null;
    return !d || drawerIsOpen();
  }

  // Keep the sticky bars + the LP "Shop Now" CTA from overlapping each other and reserve
  // bottom space so page content clears them (and the bottom-right chat bubble zone).
  function coordinateFixedUI() {
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var lp = document.querySelector('.lp-cp__sticky');
    var atc = document.querySelector('.monfiora-sticky-atc');
    var gift = document.querySelector('[data-lpfg-sticky]');
    var lpH = (mobile && lp && lp.getAttribute('data-visible') === 'true') ? (lp.offsetHeight || 0) : 0;
    var atcH = (mobile && atc && !atc.hidden && !atc.hasAttribute('hidden')) ? (atc.offsetHeight || 0) : 0;
    var giftH = (mobile && gift && gift.getAttribute('data-visible') === 'true') ? (gift.offsetHeight || 0) : 0;
    document.documentElement.style.setProperty('--lpfg-sticky-offset', (lpH + atcH) + 'px');
    var total = lpH + atcH + giftH;
    document.body.style.paddingBottom = (mobile && total > 0) ? (total + 76) + 'px' : '';
  }

  // The whole bar is one button (snippets/lp-free-gift.liquid); its label says what the tap does.
  function drawerOpenNow() {
    var mc = window.MonfioraCart;
    return mc && typeof mc.isOpen === 'function' ? !!mc.isOpen() : drawerIsOpen();
  }
  // /cart in drawer mode shows the drawer's offer panel inline (sections/main-cart-items.liquid). While
  // it is on screen the bar would only repeat it, so the bar steps away until the panel scrolls out of
  // view. The panel sits in #main-cart-items .js-contents, which every /cart render replaces: the watch
  // moves to the new panel (a MutationObserver on .js-contents, and every renderSticky). The top edge is
  // the sticky header's bottom: a strip of the panel under the header is not on screen.
  var panelIO = null;          // IntersectionObserver on the page's offer panel
  var panelEl = null;          // the panel it watches
  var panelTop = -1;           // the header height its rootMargin was built with
  var panelInView = false;     // the panel is on screen, below the sticky header
  function stickyHeaderHeight() {
    var h = document.querySelector('sticky-header');
    return h ? Math.round(h.offsetHeight || 0) : 0;
  }
  function watchPagePanel(remeasure) {
    if (typeof window.IntersectionObserver !== 'function') return;
    var panel = document.querySelector('[data-mf-cart-page] .monfiora-progress');
    if (panel === panelEl && !remeasure) return;
    var top = panel ? stickyHeaderHeight() : 0;
    if (panel === panelEl && top === panelTop) return;
    if (panelIO) panelIO.disconnect();
    panelIO = null; panelEl = panel; panelTop = top;
    if (!panel) { panelInView = false; return; }
    // Until the new observer reports, the last answer stands: the new panel is where the old one was.
    var io = new IntersectionObserver(function (entries) {
      if (io !== panelIO) return;                           // a replaced panel's late report
      var e = entries[entries.length - 1];
      if (!e || e.target !== panelEl) return;
      var seen = !!e.isIntersecting;
      if (seen === panelInView) return;
      panelInView = seen;
      if (lastState) renderSticky(lastState);
    }, { rootMargin: (top ? '-' + top + 'px' : '0px') + ' 0px 0px 0px' });
    panelIO = io;
    io.observe(panel);
  }
  function renderSticky(st) {
    var s = document.querySelector('[data-lpfg-sticky]');
    if (!s) return;
    s.hidden = false;
    watchPagePanel();
    // The open drawer is the offer's surface: the bar steps away under it (and comes back on close).
    if (drawerOpenNow()) {
      s.setAttribute('data-visible', 'false');
      s.setAttribute('inert', ''); s.setAttribute('aria-hidden', 'true');
      var liveOff = s.querySelector('[data-lpfg-sticky-live]');
      if (liveOff && lastLive) { liveOff.textContent = ''; lastLive = ''; }
      coordinateFixedUI();
      return;
    }
    var txt = s.querySelector('[data-lpfg-sticky-text]');
    var lbl = s.querySelector('[data-lpfg-sticky-label]');
    // The removal notice goes on the bar only when no other surface shows it (/cart has its own
    // status line, the toast its note): one announcement, not two.
    var noticeElsewhere = noticeHostVisible() || toastIsOpen();
    var shown = noticeActive() && !noticeElsewhere ? 'removed'
      : (st.state === 'claim' && claimBlocked()) ? 'done' : st.state;
    var text = '', label = '', visible = false;
    if (shown === 'removed') { text = removedNotice.text; label = t('sticky_cta_removed'); visible = true; }
    else if (shown === 'claim') { text = t('sticky_claim'); label = t('sticky_cta_claim'); visible = true; }
    else if (shown === 'warn') { text = warnTitle(st, 'sticky_warn'); label = t('sticky_cta_warn'); visible = true; }
    else if (shown === 'locked') { text = t('sticky_locked', { q: st.progress, t: st.threshold, n: st.remaining }); label = t('sticky_cta_locked'); visible = st.progress > 0; }
    // The text stays current, so the bar is ready when it slides back in.
    if (panelEl && panelInView) visible = false;
    s.setAttribute('data-state', shown);
    if (txt && txt.textContent !== text) txt.textContent = text;
    if (lbl && lbl.textContent !== label) lbl.textContent = label;
    s.setAttribute('data-visible', visible ? 'true' : 'false');
    // Off-screen (translated away) is still focusable: take it out of the tab order and the tree.
    // Focus leaves first ("OK" hides the bar under the finger), to the page's main region.
    if (visible) { s.removeAttribute('inert'); s.removeAttribute('aria-hidden'); }
    else {
      var ae = document.activeElement;
      if (ae && s.contains && s.contains(ae)) {
        var main = document.getElementById('MainContent');
        if (main && typeof main.focus === 'function') { try { main.focus({ preventScroll: true }); } catch (e) {} }
      }
      s.setAttribute('inert', ''); s.setAttribute('aria-hidden', 'true');
    }
    // The bar is a control, not a live region. Where nothing else announces (product and landing
    // pages, toast closed), a hidden status node says the states that need action, once each.
    var live = s.querySelector('[data-lpfg-sticky-live]');
    var say = visible && !noticeElsewhere && (shown === 'warn' || shown === 'removed' || shown === 'claim') ? text : '';
    if (live && say !== lastLive) { live.textContent = say; lastLive = say; }
    coordinateFixedUI();
  }
  function reveal(el, focusEl) {
    if (!el) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); }
    if (focusEl && typeof focusEl.focus === 'function') { try { focusEl.focus({ preventScroll: true }); } catch (e) {} }
  }
  function stickyAction() {
    var st = lastState || {};
    if (noticeActive()) { removedNotice.at = 0; if (lastCart) renderAll(lastCart, st); return; }   // "OK" dismisses
    // With the cart drawer: open it on the claim (or the offer panel); /cart in drawer mode has the
    // drawer's offer panel inline instead.
    var mc = window.MonfioraCart;
    if (drawerEl() && mc && typeof mc.refresh === 'function') {
      // The bar is the opener: closing the drawer brings focus back to it (it returns as the drawer closes).
      mc.refresh({
        open: true, opener: document.querySelector('[data-lpfg-sticky-cta]') || undefined,
        focusKey: st.state === 'claim' ? 'claim' : 'progress', source: 'sticky'
      });
      return;
    }
    var pagePanel = document.querySelector('[data-mf-cart-page] .monfiora-progress');
    if (pagePanel) {
      reveal(pagePanel, pagePanel.querySelector('input[type="radio"]:checked:not(:disabled)') || pagePanel);
      return;
    }
    var tile = document.querySelector('[data-monfiora-tiers] .monfiora-tier__input[data-tier="top"]');
    if (st.state === 'locked' && tile && !tile.disabled) {
      // On a product page: pick the 4 + 1 tile (monfiora-pdp.js listens for the bubbling change).
      if (!tile.checked) { tile.checked = true; if (typeof Event === 'function') tile.dispatchEvent(new Event('change', { bubbles: true })); }
      reveal(tile.closest ? tile.closest('[data-monfiora-tiers]') : tile, tile);
      return;
    }
    var m = document.querySelector('[data-lp-gift]');
    if (m) { reveal(m, m.querySelector('[data-lpfg-variant]') || m.querySelector('[data-lpfg-remove]')); return; }
    window.location.href = cartUrl();
  }

  function injectNote(st) {
    var n = document.getElementById('cart-notification');
    if (!n || !n.classList.contains('active')) return;
    var ex = n.querySelector('.lpfg-note');
    var msg = '', warnKind = false, link = '';
    if (noticeActive()) { msg = removedNotice.text; warnKind = true; }
    else if (st.state === 'warn') { msg = warnText(st); warnKind = true; link = t('note_link_warn'); }
    else if (st.state === 'claim' && !claimBlocked()) { msg = t('note_claim'); link = t('note_link_claim'); }
    else if (st.state === 'locked' && st.progress) msg = pl('note_remaining_one', 'note_remaining', st.remaining);
    if (!msg) { if (ex) ex.remove(); return; }
    var sig = (warnKind ? 'w' : 'i') + '|' + msg + '|' + link;
    if (ex && ex.getAttribute('data-sig') === sig) return; // unchanged — no churn
    if (ex) ex.remove();
    var note = document.createElement('div');
    note.className = 'lpfg-note' + (warnKind ? ' lpfg-note--warn' : '');
    note.setAttribute('role', 'status');
    note.setAttribute('data-sig', sig);
    var span = document.createElement('span'); span.textContent = msg;
    note.appendChild(span);
    if (link) {
      var a = document.createElement('a'); a.className = 'lpfg-note__link'; a.href = cartUrl(); a.textContent = link;
      a.addEventListener('click', function (e) { var m = document.querySelector('[data-lp-gift]'); if (m) { e.preventDefault(); reveal(m); } });
      note.appendChild(a);
    }
    var anchor = n.querySelector('.cart-notification__links') || n.querySelector('#cart-notification-product');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor);
  }

  /* ---------- theme surfaces Dawn does not repaint after an EXTERNAL cart write ---------- */
  function sectionInner(html, selector) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector) || doc.querySelector('.shopify-section');
  }
  function bubbleNeedsRepair(cart) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return false;
    var count = bubble.querySelector('.cart-count-bubble');
    if ((cart.item_count > 0) !== !!count) return true;            // count appeared / disappeared
    if (!count) return false;
    if (!count.querySelector('.visually-hidden')) return true;      // label stripped by a foreign writer
    var shown = count.querySelector('[aria-hidden="true"]');
    var digits = shown ? parseInt(String(shown.textContent).replace(/\D/g, ''), 10) : NaN;
    return cart.item_count < 100 && digits !== cart.item_count;     // number itself is wrong
  }
  function cartPageNeedsRender(cart) {
    var host = document.getElementById('main-cart-items');
    if (!host || !host.dataset || !host.dataset.id) return false;
    var ae = document.activeElement;
    if (ae && host.contains && host.contains(ae) && /^(input|select|textarea)$/i.test(ae.tagName)) return false; // customer is typing
    // Gift rows carry a hidden .quantity__input (no stepper), so every line is counted here.
    var dom = [].map.call(host.querySelectorAll('.quantity__input[data-quantity-variant-id]'), function (i) {
      return i.getAttribute('data-quantity-variant-id') + 'x' + (i.value || i.getAttribute('value'));
    }).sort().join('|');
    var api = (cart.items || []).map(function (it) { return it.variant_id + 'x' + it.quantity; }).sort().join('|');
    return dom !== api;
  }
  // The drawer was rendered from another cart (a write it did not hear: Standard Actions, the LP
  // module claim, a back/forward-cache return). Its body carries item_count:total_price.
  function drawerNeedsRender(cart) {
    var d = drawerEl();
    if (!d) return false;
    var ae = document.activeElement;
    if (ae && d.contains && d.contains(ae) && /^(input|select|textarea)$/i.test(ae.tagName || '') &&
        String(ae.getAttribute && ae.getAttribute('type')) !== 'radio') return false;   // a quantity being typed
    var body = d.querySelector('[data-cart-sig]');
    if (!body) return (cart.item_count || 0) > 0;                   // the empty drawer, items in the cart
    return body.getAttribute('data-cart-sig') !== cartSig(cart);
  }
  function renderSections(cart) {
    if (holdCount > 0 || holdsPending > 0) return;              // R10: no repair while a hold is open
    var ids = [];
    var toast = document.getElementById('cart-notification');
    var toastOpen = !!(toast && toast.classList.contains('active'));
    if (toastOpen && toastCount !== cart.item_count && document.getElementById('cart-notification-button')) ids.push('cart-notification-button');
    var bubble = bubbleNeedsRepair(cart);
    if (bubble) ids.push('cart-icon-bubble');
    var cartPage = cartPageNeedsRender(cart);
    if (cartPage) cartSectionIds().forEach(function (id) { if (id !== 'cart-icon-bubble' && ids.indexOf(id) < 0) ids.push(id); });
    var drawer = drawerNeedsRender(cart);
    if (drawer && ids.indexOf('cart-drawer') < 0) ids.push('cart-drawer');
    if (!ids.length) return;
    if (toastOpen) toastCount = cart.item_count;
    // No Accept: application/json here — on a product URL Shopify would answer with the product's
    // own JSON instead of the requested sections (measured live 2026-09-14).
    fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) {
        if (!sections) return;
        var btn = document.getElementById('cart-notification-button');
        var btnSrc = sectionInner(sections['cart-notification-button'], '.shopify-section');
        if (btn && btnSrc) btn.innerHTML = btnSrc.innerHTML;
        if (sections['cart-icon-bubble']) {
          bubbleGuard = true;                       // our own write; the observer must not refresh again
          updateBubble(sections);
          setTimeout(function () { bubbleGuard = false; }, 0);
        }
        if (drawer && !cartPage) swapDrawer(sections);
        if (cartPage) {
          swapCartSections(sections);
          var empty = cart.item_count === 0;
          var items = document.getElementById('main-cart-items');
          var footer = document.getElementById('main-cart-footer');
          if (items && items.classList) items.classList.toggle('is-empty', empty);
          if (footer && footer.classList) footer.classList.toggle('is-empty', empty);
        }
      })
      .catch(function () {});
  }

  /* ---------- actions ---------- */
  function reloadLazyImages(container) {
    if (!container) return;
    var imgs = container.querySelectorAll('img[loading="lazy"]');
    if (!imgs.length) return;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    [].forEach.call(imgs, function (img) {
      if (img.complete && img.naturalWidth > 0) return;
      var r = img.getBoundingClientRect();
      if (r.top < vh + 300 && r.bottom > -300) img.loading = 'eager';
    });
  }
  function updateBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    var src = sectionInner(sections['cart-icon-bubble'], '#cart-icon-bubble');
    if (src) { bubble.innerHTML = src.innerHTML; reloadLazyImages(bubble); }
  }
  function cartSectionIds() {
    var ids = ['cart-icon-bubble'];
    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) ids.push(items.dataset.id);
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    if (drawerEl()) ids.push('cart-drawer');
    return ids;
  }
  // Repaint the drawer from a section response: its own update() keeps focus, the hold's button and
  // the empty state right (assets/cart-drawer.js); without it, swap the panel's contents.
  function swapDrawer(sections) {
    var d = drawerEl();
    if (!d || !sections || !sections['cart-drawer']) return;
    if (typeof d.update === 'function') { d.update({ 'cart-drawer': sections['cart-drawer'] }, { source: 'lpfg' }); return; }
    var src = sectionInner(sections['cart-drawer'], '.drawer__inner');
    var dst = d.querySelector('.drawer__inner');
    if (src && dst && src !== dst) { dst.innerHTML = src.innerHTML; reloadLazyImages(dst); }
  }
  function swapCartSections(sections) {
    if (!sections) return;
    updateBubble(sections);
    swapDrawer(sections);
    [['main-cart-items', '.js-contents'], ['main-cart-footer', '.js-contents']].forEach(function (pair) {
      var host = document.getElementById(pair[0]);
      if (!host || !host.dataset.id) return;
      var raw = sections[host.dataset.id];
      if (!raw) return;
      var doc = new DOMParser().parseFromString(raw, 'text/html');
      var src = doc.querySelector(pair[1]);
      var dst = host.querySelector(pair[1]);
      if (src && dst) { dst.innerHTML = src.innerHTML; reloadLazyImages(dst); }
      else if (window.console) console.warn('[lp-free-gift] swapCartSections: missing', pair[0], pair[1]);
    });
    var cartItems = document.querySelector('cart-items, cart-drawer-items');
    if (cartItems) cartItems.classList.remove('is-empty');
  }
  function firstVariantId(handle) {
    var p = products[handle];
    if (!p || !p.variants || !p.variants.length) return null;
    var av = p.variants.filter(function (v) { return v.available; });
    return (av[0] || p.variants[0]).id;
  }
  function claim(el, btn) {
    if (claiming) return;
    if (!lastState || lastState.claimable < 1 || claimBlocked()) { refresh(); return; }   // nothing to claim
    var varSel = el.querySelector('[data-lpfg-variant]');
    var prodSel = el.querySelector('[data-lpfg-product]');
    var variantId = varSel && varSel.value ? parseInt(varSel.value, 10) : null;
    if (variantId) { addGift(el, btn, variantId); return; }
    var orig = btn.textContent; btn.disabled = true; btn.textContent = t('adding');
    ensureCatalog().then(function () {
      wireModule(el);
      var vs = el.querySelector('[data-lpfg-variant]');
      var handle = (prodSel && prodSel.value) || (CFG.products || [])[0];
      var id = (vs && vs.value) ? parseInt(vs.value, 10) : firstVariantId(handle);
      btn.disabled = false; btn.textContent = orig;
      if (id) addGift(el, btn, id);
      else if (window.console) console.warn('[lp-free-gift] claim: no resolvable variant');
    }).catch(function (err) {
      btn.disabled = false; btn.textContent = orig;
      if (window.console) console.warn('[lp-free-gift] catalog load failed', err);
    });
  }
  function addGift(el, btn, variantId) {
    if (claiming) return;
    claiming = true;
    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = t('adding');
    var props = {}; props[GIFT_PROP] = 'true';
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: props }], sections: cartSectionIds(), sections_url: window.location.pathname })
    }).then(function (r) { return r.json(); })
      .then(function (data) {
        // Refused (e.g. 422, the free piece sold out): nothing was added. Say so instead of
        // leaving the same prompt up.
        if (!data || !data.items) { claimRefusedAt = Date.now(); forgetCatalog(); return; }
        swapCartSections(data.sections); justClaimed = true; removedNotice = null;
      })
      .catch(function (err) { if (window.console) console.warn('[lp-free-gift] claim failed', err); })
      .finally(function () { claiming = false; btn.disabled = false; btn.textContent = orig; refresh(); });
  }
  // The warn state's "Remove free piece": removes exactly the excess units, like the automatic path.
  function removeGift(btn) {
    if (!lastState || !lastState.excess) { refresh(); return; }
    btn.disabled = true;
    var p = runRemovals(lastState);
    p.then(function () { btn.disabled = false; });
  }

  /* ---------- reconcile: take out free pieces the cart no longer earns ---------- */
  function onCartPage() { return !!document.getElementById('main-cart-items'); }
  function toastIsOpen() { var n = document.getElementById('cart-notification'); return !!(n && n.classList.contains('active')); }
  // ms to wait: Dawn is mid-update (its own .cart__items--disabled), or the customer tapped in the
  // line items less than QUIET_MS ago. A removal now would shift Dawn's line numbers under a pending change.
  function cartBusyFor() {
    var hosts = hostEls();
    var ae = document.activeElement;
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (host.classList && host.classList.contains('cart__items--disabled')) return QUIET_MS;
      // A quantity typed but not sent yet (focus still in the field): a section swap would wipe it.
      if (ae && host.contains && host.contains(ae) && /^input$/i.test(ae.tagName || '') &&
          ae.getAttribute && ae.getAttribute('value') != null && String(ae.value) !== String(ae.getAttribute('value'))) return QUIET_MS;
    }
    var wait = QUIET_MS - (Date.now() - lastInteraction);
    return wait > 0 ? wait : 0;
  }
  // Returns true when it took over rendering (a removal is running).
  function maybeReconcile(cart, st) {
    if (!st.excess || claiming || reconciling) return false;
    if (holdsPending > 0) return false;                      // R10: a swap still running (past the cap)
    if (!(onCartPage() || toastIsOpen() || drawerIsOpen())) return false;    // only where the customer can see the notice
    var wait = cartBusyFor();
    if (wait) { scheduleRefresh(wait + 50); return false; }  // render the warn state meanwhile
    var sig = (cart.token || '') + '|' + st.removals.map(function (r) { return r.key + ':' + r.to; }).join(',');
    if (attempts[sig] && Date.now() - attempts[sig] < RETRY_MS) return false;
    attempts[sig] = Date.now();
    runRemovals(st);
    return true;
  }
  function runRemovals(st) {
    if (reconciling) return reconciling;
    if (!st.removals || !st.removals.length) return Promise.resolve();
    var host = document.getElementById('main-cart-items');
    hostEls().forEach(function (h) { if (h.classList) h.classList.add('cart__items--disabled'); });   // Dawn: pointer-events none
    var plan = st.removals || [], last = null, chain = Promise.resolve();
    var send = rawFetch || window.fetch;
    plan.forEach(function (r, i) {
      chain = chain.then(function () {
        var body = { id: r.key, quantity: r.to };
        if (i === plan.length - 1) { body.sections = cartSectionIds(); body.sections_url = window.location.pathname; }
        return send('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
        }).then(function (res) {
          return res.json().then(function (j) {
            if (!res.ok || !j || !j.items) throw new Error((j && (j.description || j.message)) || 'HTTP ' + res.status);
            last = j;
          });
        });
      });
    });
    reconciling = chain.then(function () {
      var units = st.excess, first = plan[0];
      // "It needs 4 paid pieces" is only true of a cart that held one free-piece unit; otherwise the
      // notice names no count and no rule (R8). The discount not applying is its own reason.
      var title1 = { title: first ? first.title : '' };
      var text = units !== 1 ? t('removed_many')
        : st.offerOff ? t('removed_off', title1)
        : st.giftUnits === 1 ? t('removed_one', title1) : t('removed_many');
      // The rule was met but Shopify charged the free piece: a claim now would come back charged too,
      // so the claim prompt waits (claim_failed) instead of promising Rs. 0.
      if (st.offerOff) blockClaims();
      removedNotice = { text: text, at: Date.now(), sig: cartSig(last) };
      var ae = document.activeElement;
      var hadFocus = !!(ae && host && host.contains && host.contains(ae));
      // Line ids are index-based (Quantity-N) and a removed line renumbers the rest: remember the
      // variant of the focused quantity field, not its id.
      var focusVariant = hadFocus && ae.getAttribute ? ae.getAttribute('data-quantity-variant-id') : null;
      var focusNth = focusVariant ? [].indexOf.call(variantFields(focusVariant), ae) : -1;   // split lines share a variant
      bubbleGuard = true;
      swapCartSections(last.sections);
      setTimeout(function () { bubbleGuard = false; }, 0);
      if (hadFocus) restoreFocus(focusVariant, focusNth);
      var empty = last.item_count === 0;
      var footer = document.getElementById('main-cart-footer');
      if (host && host.classList) host.classList.toggle('is-empty', empty);
      if (footer && footer.classList) footer.classList.toggle('is-empty', empty);
      var items = document.querySelector('cart-items');
      if (items && items.classList) items.classList.toggle('is-empty', empty);
      if (toastIsOpen()) { toastOpening = false; toastCount = null; }   // its "View cart (n)" must drop too
      renderAll(last, computeState(last));
      setTimeout(function () { if (lastCart && lastState) renderAll(lastCart, lastState); }, NOTICE_MS + 50);
    }).catch(function (err) {
      if (window.console) console.warn('[lp-free-gift] removing a free piece failed', err);
      scheduleRefresh();                                    // re-read what Shopify actually holds
    }).then(function () {
      if (host && host.classList) host.classList.remove('cart__items--disabled');
      hostEls().forEach(function (h) { if (h.classList) h.classList.remove('cart__items--disabled'); });   // the swap may have replaced them
      reconciling = null;
      if (pendingRefresh) { pendingRefresh = false; scheduleRefresh(); }
    });
    return reconciling;
  }

  // The swap replaced the control that had focus: put it back on the same control if it survived,
  // else on the status line that explains what changed.
  function variantFields(variantId) {
    return [].filter.call(document.querySelectorAll('#main-cart-items .quantity__input[data-quantity-variant-id="' + variantId + '"]'),
      function (f) { return f.getAttribute('type') !== 'hidden'; });
  }
  function restoreFocus(variantId, nth) {
    var el = null;
    if (variantId) {
      var fields = variantFields(variantId);
      el = fields[nth >= 0 && nth < fields.length ? nth : 0] || null;
    }
    if (!el) {
      el = document.querySelector('[data-lpfg-notice]');
      if (el && el.setAttribute) el.setAttribute('tabindex', '-1');
    }
    if (el && typeof el.focus === 'function') { try { el.focus({ preventScroll: true }); } catch (e) {} }
  }

  // Shopify can split a cart line: one more piece of a fragrance whose other pieces carry the
  // "Buy 4, Get 1 Free" allocation becomes its own line, yet /cart/add.js answers with the OLD
  // line's key (measured 3 Oct 2026), so Dawn's toast showed "Qty 4 · Rs. 1,676.00" for a 1-piece
  // add. Show the line that actually grew, from the same response's section HTML.
  function fixToastRow(add, cart) {
    if (!add || !add.json || !add.json.key || now() - add.at > 15000) return;
    var grown = [];
    if (add.before) {
      var before = {};
      (add.before.items || []).forEach(function (it) { before[it.key] = (before[it.key] || 0) + it.quantity; });
      grown = (cart.items || []).filter(function (it) { return !isGiftLine(it) && it.quantity > (before[it.key] || 0); });
    } else if (add.req && add.req.id) {
      // No earlier cart read (the add came straight after the page loaded): only the clear split case —
      // two lines of this variant, the answered one plus one holding exactly the quantity just added.
      var same = (cart.items || []).filter(function (it) { return !isGiftLine(it) && String(it.variant_id) === String(add.req.id); });
      var other = same.filter(function (it) { return it.key !== add.json.key && it.quantity === add.req.quantity; });
      if (same.length === 2 && other.length === 1) grown = other;
    }
    if (grown.length !== 1 || grown[0].key === add.json.key) return;
    var html = add.json.sections && add.json.sections['cart-notification-product'];
    var target = document.getElementById('cart-notification-product');
    if (!html || !target) return;
    var node = new DOMParser().parseFromString(html, 'text/html').querySelector('[id="cart-notification-product-' + grown[0].key + '"]');
    if (node) target.innerHTML = node.innerHTML;
  }

  // The variant and quantity of a single-line add (Dawn posts FormData; others post JSON).
  function addRequest(init) {
    try {
      var b = init && init.body;
      if (!b) return null;
      if (typeof b.get === 'function') return { id: b.get('id'), quantity: parseInt(b.get('quantity'), 10) || 1 };
      if (typeof b === 'string') {
        var j = JSON.parse(b);
        if (j && j.id && !j.items) return { id: j.id, quantity: parseInt(j.quantity, 10) || 1 };
      }
    } catch (e) {}
    return null;
  }

  /* ---------- refresh ---------- */
  function renderAll(cart, st, readAt) {
    // When the read behind this cart started: a /cart.js read passes it; a removal response is the
    // server cart as of now; a repaint of the same cart object keeps the old time.
    if (typeof readAt === 'number') lastReadAt = readAt;
    else if (cart !== lastCart) lastReadAt = now();
    lastCart = cart;
    lastState = st;
    if (toastOpening && toastIsOpen()) {
      // A read that started before the add answered cannot show which line grew: wait for the next one.
      if (lastAdd && typeof readAt === 'number' && readAt < lastAdd.at) scheduleRefresh();
      else { toastCount = cart.item_count; toastOpening = false; fixToastRow(lastAdd, cart); lastAdd = null; }
    }
    modules.forEach(function (el) { wireModule(el); renderModule(el, st); });
    updateOfferBar(st);
    renderNotice(cart);
    renderSticky(st);
    injectNote(st);
    renderSections(cart);
    settleWaiters(cart, st, lastReadAt);
    emitState(cart, st);
  }
  /* ---------- read-only API for other theme scripts (window.MonfioraFreeGift) ---------- */
  // Resolves the nextState() promises whose cart write this read already includes.
  function settleWaiters(cart, st, readAt) {
    if (!stateWaiters.length) return;
    stateWaiters = stateWaiters.filter(function (w) {
      if (w.done) return false;
      if (readAt <= w.mark) return true;                    // read started before that write landed
      w.done = true;
      w.resolve({ cart: cart, state: st });
      return false;
    });
  }
  function emitState(cart, st) {
    if (typeof document.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') return;
    try { document.dispatchEvent(new CustomEvent('lpfg:state', { detail: { cart: cart, state: st } })); } catch (e) {}
  }
  // {cart, state} from the first render whose /cart.js read started after the latest cart write the
  // network hook had seen when this was called; null after timeoutMs (default 4 s).
  function nextState(timeoutMs) {
    var ms = typeof timeoutMs === 'number' && timeoutMs >= 0 ? timeoutMs : 4000;
    var mark = writeAt;
    return new Promise(function (resolve) {
      var w = { mark: mark, resolve: resolve, done: false };
      stateWaiters.push(w);
      setTimeout(function () {
        if (w.done) return;
        w.done = true;
        stateWaiters = stateWaiters.filter(function (x) { return x !== w; });
        resolve(null);
      }, ms);
    });
  }
  // Run fn while our own #cart-icon-bubble observer stays quiet (the caller is swapping the bubble
  // from a response it already has, so no second /cart.js read is needed).
  function quiet(fn) {
    bubbleGuard = true;
    try { if (typeof fn === 'function') fn(); }
    finally { setTimeout(function () { bubbleGuard = false; }, 0); }
  }
  // R10: pause reads, renders, removals and repairs until promise settles (each hold releases once; a
  // hold that never settles is released after HOLD_CAP_MS). Then one fresh read. The cap gives back
  // reads and renders only: removals and a held Check out wait for the promise itself (holdsPending),
  // because a slow swap past the cap still has its old line to remove.
  function hold(promise) {
    holdCount++;
    holdsPending++;
    var released = false;
    function release() {
      if (released) return;
      released = true;
      holdCount = Math.max(0, holdCount - 1);
      if (!holdCount) { pendingRefresh = false; scheduleRefresh(0); }
    }
    function settle() {
      holdsPending = Math.max(0, holdsPending - 1);
      if (released && !holdCount) scheduleRefresh(0);       // capped earlier: removals may run now
      release();
    }
    setTimeout(release, HOLD_CAP_MS);
    Promise.resolve(promise).then(settle, settle);
    return promise;
  }
  // One /cart.js read at a time. A request that arrives mid-flight is not dropped: it queues
  // exactly one follow-up, so the last write always wins.
  function refresh() {
    if (holdCount > 0) { pendingRefresh = true; return Promise.resolve(); }   // R10: one read when it ends
    if (refreshing || reconciling) { pendingRefresh = true; return Promise.resolve(); }
    refreshing = true;
    pendingRefresh = false;
    cartDirty = false;
    var readAt = now();
    function done() { refreshing = false; if (pendingRefresh && !reconciling) { pendingRefresh = false; scheduleRefresh(); } }
    return getCart().then(function (cart) {
      if (!cart) return;
      // A hold opened while this read was in flight: the cart may be mid-swap. Read again when it ends.
      if (holdCount > 0) { pendingRefresh = true; return; }
      if (!lastCart) lastCart = cart;                       // the "before" for the next add's toast check
      var go = function () {
        var st = computeState(cart);
        if (justClaimed) { justClaimed = false; if (st.excess) blockClaims(); }   // the claim came back charged
        if (pickersStale) { pickersStale = false; modules.forEach(populateVariants); }
        if (!maybeReconcile(cart, st)) renderAll(cart, st, readAt);
      };
      // Load the heavy catalog only the first time it's actually needed.
      if (!catalogLoaded && (modules.length || cart.item_count > 0)) return ensureCatalog().then(go);
      go();
    }).then(done, function (err) { if (window.console) console.warn('[lp-free-gift] refresh failed', err); done(); });
  }
  // Trailing debounce. Also used as an event listener, so only a number counts as a delay.
  function scheduleRefresh(delay) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { refreshTimer = null; refresh(); }, typeof delay === 'number' ? delay : 250);
  }

  // A free piece the cart no longer earns must not reach checkout. Every checkout from the cart page
  // and the toast is held: Dawn's pending quantity change must land first (a tap on − is sent 300 ms
  // later), then the SERVER cart is read, any excess free piece removed, and the customer goes to
  // /checkout with that cart. The form is never re-posted: its quantities can be stale (Dawn leaves the
  // field at the new value when its own request fails), and Shopify would apply them after this check
  // (measured 3 Oct 2026, a failed − request then Check out reached checkout at ₹1,554.20). If the
  // customer touches the cart during the check, it runs again. A second tap while held is swallowed.
  function cartSettled() {
    if (reconciling || holdCount > 0 || holdsPending > 0) return false;
    var hosts = hostEls();
    for (var i = 0; i < hosts.length; i++) if (hosts[i].classList && hosts[i].classList.contains('cart__items--disabled')) return false;
    return Date.now() - lastInteraction >= SETTLE_MS;
  }
  function whenSettled(maxMs) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function poll() { if (cartSettled() || Date.now() - start > maxMs) resolve(); else setTimeout(poll, 100); })();
    });
  }
  function releaseButton() {
    if (!heldButton) return;
    var b = heldButton.el;
    if (b.removeAttribute) b.removeAttribute('aria-busy');
    if (heldButton.label != null && T.checking && b.textContent === t('checking')) b.textContent = heldButton.label;
    heldButton = null;
  }
  // "We could not check your cart…" goes right under the Check out button that was pressed — on a phone
  // the /cart status line can be a screen away — as an alert, since the customer is waiting on it.
  function clearHoldError() {
    [].forEach.call(document.querySelectorAll('.lpfg-hold-error'), function (n) { n.remove(); });
  }
  function showHoldError(anchor) {
    clearHoldError();
    var parent = anchor && anchor.parentNode;
    if (!parent) return;
    var msg = document.createElement('p');
    msg.className = 'lpfg-hold-error';
    msg.setAttribute('role', 'alert');
    msg.textContent = t('hold_failed');
    parent.appendChild(msg);
  }
  // /cart.js that rejects on anything but a JSON cart (a 429 or a challenge page is not a cart).
  function readCartStrict() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (c) { if (!c || !c.items) throw new Error('not a cart'); return c; });
  }
  // The hold lock: every line-item host plus every [data-lpfg-lock-scope] (the drawer body, so its
  // free-piece picker and suggestions take no taps either). The drawer scope is also made inert.
  function holdLock(on) {
    hostEls().forEach(function (h) { if (h.classList) h.classList.toggle('lpfg-hold-lock', on); });
    [].forEach.call(document.querySelectorAll('[data-lpfg-lock-scope]'), function (s) {
      if (s.classList) s.classList.toggle('lpfg-hold-lock', on);
      if (on) s.setAttribute('inert', ''); else s.removeAttribute('inert');
    });
  }
  function holdCheckout(e) {
    var f = e.target;
    if (!f || CHECKOUT_FORMS.indexOf(f.id) < 0) return;
    e.preventDefault();
    if (holding) return;                                    // second tap while held
    var seq = ++holdSeq;
    var btn = e.submitter && e.submitter.form === f ? e.submitter
      : (f.querySelector && f.querySelector('[name="checkout"]')) || document.querySelector('[name="checkout"][form="' + f.id + '"]');
    clearHoldError();
    if (btn && btn.setAttribute) {
      // A press queued by the inline pre-load guard already shows "Checking your cart…": keep its label.
      heldButton = { el: btn, label: e.label != null ? e.label : btn.textContent };
      btn.setAttribute('aria-busy', 'true');
      if (T.checking) btn.textContent = t('checking');
    }
    function alive() { return seq === holdSeq; }
    // While the server cart is read and fixed, the line items take no taps: Dawn sends a change by line
    // NUMBER 300 ms after a tap, and a removal by key would shift it onto the wrong line. Looked up on
    // every call: a removal re-renders the drawer's lines, and the new ones must stay locked too.
    function lock(on) { holdLock(!!on); }
    var deadline = now() + HOLD_MAX_MS;
    var finished = false;
    function finish(ok) {
      if (finished || !alive()) return;
      finished = true;
      if (ok) {
        // The lines stay locked while checkout loads: nothing may change the cart after it was checked.
        window.location.href = '/checkout';                 // the server cart, exactly as just checked
        // Still here 8 s later: the navigation was cancelled (Esc, stop). Give the button back.
        setTimeout(function () { if (alive()) { lock(false); holding = null; releaseButton(); } }, 8000);
        return;
      }
      // The cart could not be checked (rate limit, offline, a refused removal). Never send an unchecked
      // cart to checkout: say so right under the button and let the customer tap again.
      lock(false);
      holding = null;
      var where = heldButton && heldButton.el;
      releaseButton();
      showHoldError(where || f);
    }
    // Read the server cart; remove any free piece it no longer earns; read again until it is clean.
    function verify(attempt) {
      return readCartStrict().then(function (c) {
        var st = computeState(c);
        if (!st.excess) return true;
        if (attempt >= 3) return false;
        return runRemovals(st).then(function () { return verify(attempt + 1); });
      }, function () {
        if (attempt >= 3 || now() > deadline - 1500) return false;
        return wait(600 * (attempt + 1)).then(function () { return verify(attempt + 1); });
      });
    }
    function check() {
      var mark = lastInteraction;
      return whenSettled(Math.max(0, deadline - now() - 3000))
        // Still mid-swap after the wait: the cart briefly holds the old and the new free-piece line, and
        // removing one now would leave none once the swap removes its old line. Not checked: tap again.
        .then(function () { if (!alive() || holdsPending > 0) return false; lock(true); return verify(0); })
        .then(function (clean) {
          if (!clean) return false;
          // Touched before the lock took hold: the server may not have that change yet. Check again.
          if ((lastInteraction !== mark || !cartSettled()) && now() < deadline - 2500) { lock(false); return check(); }
          return true;
        });
    }
    setTimeout(function () { finish(false); }, HOLD_MAX_MS);
    holding = check().then(finish, function () { finish(false); });
  }

  /* ---------- network hook: hear cart writes from ANY script ---------- */
  function isCartMutationUrl(url) {
    return /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?(?:[?#]|$)/i.test(String(url || ''));
  }
  function installNetworkHook() {
    if (window.__lpfgNetHook) return;
    window.__lpfgNetHook = true;
    var nativeFetch = window.fetch;
    rawFetch = function () { return nativeFetch.apply(window, arguments); };
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && typeof input.url === 'string') ? input.url : String(input || '');
      var p = nativeFetch.apply(this, arguments);
      if (isCartMutationUrl(url)) {
        var before = lastCart;
        var init = arguments[1];
        p = p.then(function (r) {
          if (r && r.ok) {
            cartDirty = true;
            writeAt = now();
            if (/\/cart\/add/i.test(url) && typeof r.clone === 'function') {
              var at = now();                               // the add is committed now
              var req = addRequest(init);
              r.clone().json().then(function (j) { lastAdd = { json: j, before: before, at: at, req: req }; }).catch(function () {});
            }
            scheduleRefresh();
          }
          return r;
        });
      }
      return p;
    };
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && XHR.prototype.open && XHR.prototype.send) {
      var open = XHR.prototype.open, send = XHR.prototype.send;
      XHR.prototype.open = function (method, url) { this.__lpfgCartWrite = isCartMutationUrl(url); return open.apply(this, arguments); };
      XHR.prototype.send = function () {
        if (this.__lpfgCartWrite) this.addEventListener('loadend', function () { if (this.status >= 200 && this.status < 300) { cartDirty = true; writeAt = now(); scheduleRefresh(); } });
        return send.apply(this, arguments);
      };
    }
  }

  /* ---------- init + listeners ---------- */
  function init() {
    modules = [].slice.call(document.querySelectorAll('[data-lp-gift]'));
    var s = document.querySelector('[data-lpfg-sticky]');
    var cta = s && s.querySelector('[data-lpfg-sticky-cta]');
    if (cta) cta.addEventListener('click', stickyAction);

    var lp = document.querySelector('.lp-cp__sticky');
    if (lp && 'MutationObserver' in window) new MutationObserver(coordinateFixedUI).observe(lp, { attributes: true, attributeFilter: ['data-visible'] });
    var atc = document.querySelector('.monfiora-sticky-atc');
    if (atc && 'MutationObserver' in window) new MutationObserver(coordinateFixedUI).observe(atc, { attributes: true, attributeFilter: ['hidden'] });

    // The toast opening is the moment to check the cart: its note, and any removal, happen while it is in view.
    var notif = document.getElementById('cart-notification');
    if (notif && 'MutationObserver' in window) new MutationObserver(function () {
      if (notif.classList.contains('active')) { toastOpening = true; refresh(); }
    }).observe(notif, { attributes: true, attributeFilter: ['class'] });

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && 'MutationObserver' in window) new MutationObserver(function () { if (!bubbleGuard) scheduleRefresh(); }).observe(bubble, { childList: true, subtree: true, characterData: true });

    // The drawer opening is a moment to check the cart, like the toast: a removal it needs happens in
    // view, and a stale drawer is repaired. Debounced, so it merges with the read an add schedules.
    var drawer = drawerEl();
    if (drawer && 'MutationObserver' in window) {
      var wasOpen = drawerIsOpen();
      new MutationObserver(function () {
        var open = drawerIsOpen();
        if (open !== wasOpen && lastState) renderSticky(lastState);   // R10: the bar steps away, then returns
        if (open && !wasOpen) scheduleRefresh();
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    // R10: Dawn's quick-add modal releases the page's scroll lock as it closes, even over the open drawer.
    if (document.body && typeof document.body.addEventListener === 'function') {
      document.body.addEventListener('modalClosed', function () {
        var mc = window.MonfioraCart;
        if (mc && typeof mc.syncLock === 'function') setTimeout(function () { mc.syncLock(); }, 0);
      });
    }
    function markInteraction(e) {
      var tg = e.target;
      if (inHost(tg)) lastInteraction = Date.now();
    }
    document.addEventListener('click', markInteraction, true);
    document.addEventListener('input', markInteraction, true);
    document.addEventListener('change', markInteraction, true);
    document.addEventListener('submit', holdCheckout, true);
    // Check out pressed before this deferred script ran was queued by the inline guard in
    // snippets/lp-free-gift.liquid: give it the same check now.
    window.__lpfgReady = true;
    if (window.__lpfgQueued) { var queued = window.__lpfgQueued; window.__lpfgQueued = null; holdCheckout(queued); }
    window.addEventListener('pageshow', function (ev) {
      if (ev && ev.persisted) {                              // back from checkout: the button works again
        holdSeq++; holding = null; releaseButton();
        var items = document.getElementById('main-cart-items');
        if (items && items.classList) items.classList.remove('lpfg-hold-lock');
        holdLock(false);                                     // every host and drawer scope
      }
      scheduleRefresh();
    });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) scheduleRefresh(); });
    window.addEventListener('resize', coordinateFixedUI);
    // /cart in drawer mode: each render replaces .js-contents and the offer panel in it. The header's
    // height changes at 990px, so a resize re-measures the panel's top edge.
    var pageContents = document.querySelector('[data-mf-cart-page] #main-cart-items .js-contents');
    if (pageContents && 'MutationObserver' in window) new MutationObserver(function () { watchPagePanel(); }).observe(pageContents, { childList: true });
    window.addEventListener('resize', function () { watchPagePanel(true); });
    watchPagePanel();                                        // before the first read, so the bar never flashes in
    setTimeout(coordinateFixedUI, 1500);
    setTimeout(coordinateFixedUI, 4000);

    // On /cart the first read runs at once (a stale free piece is removed before checkout is likely);
    // elsewhere it waits for idle.
    if (onCartPage()) refresh();
    else idle(refresh);
  }

  window.MonfioraFreeGift = {
    getCart: function () { return lastCart; },
    getState: function () { return lastState; },
    nextState: nextState,
    quiet: quiet,
    // A claim made elsewhere (the drawer) landed: treated like our own, so a charged one blocks claims.
    markClaim: function () { justClaimed = true; },
    claimBlocked: claimBlocked,
    hold: hold
  };

  installNetworkHook(); // as early as possible — before any app can write to the cart
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
